/* eslint-disable @typescript-eslint/no-explicit-any */
import { Request } from 'aws-sdk';

import { ExtractOverloadedParams, FilterUnknowns, TupleArrayUnion } from './localstack';

/** Extract response type from AWS SDK method */
export type ExtractAwsSdkResponse<T> =
  T extends Request<infer R, any> ? R : T extends Promise<infer R> ? R : ExtractOutput<T>;

type ExtractOutput<T> = NonNullable<T> extends { promise: () => Promise<infer R> } ? R : never;

/** Extract Name=Method mapping from object/instance */
export type ExtractMethodsRecord<T> = Pick<
  T,
  { [K in keyof T]: T[K] extends (...args: any) => any ? K : never }[keyof T]
>;

// Type helper to extract method names from Commands module
type ExtractCommandMethods<T> = {
  [K in keyof T]: K extends `${infer Method}Command` ? Uncapitalize<Method> : never;
}[keyof T];

// Extract the actual input type from a Command class
type CommandInput<T> = T extends new (input: infer Input) => any ? Input : never;

// Extract the actual output type from a Command class
type CommandOutput<T> = T extends new (input: any) => infer Instance
  ? Instance extends { resolveMiddleware(...args: any[]): any }
    ? Awaited<ReturnType<Instance['resolveMiddleware']>> extends (...args: any[]) => infer Result
      ? Awaited<Result> extends { output: infer Output }
        ? Output
        : any
      : any
    : any
  : any;

// Map command names back to their Command class keys
type MethodToCommand<K, CommandsModule> = {
  [P in keyof CommandsModule]: P extends `${Capitalize<K & string>}Command` ? P : never;
}[keyof CommandsModule];

// Create a method record for v3 clients with proper input and output types
export type V3MethodsRecord<CommandsModule> = {
  [K in ExtractCommandMethods<CommandsModule>]: (
    ...args: [CommandInput<CommandsModule[MethodToCommand<K, CommandsModule>]>]
  ) => { promise: () => Promise<CommandOutput<CommandsModule[MethodToCommand<K, CommandsModule>]>> };
};

export type AwsClientContext<
  Client extends keyof Clients,
  Clients extends Record<string, any>,
  Commands extends Record<string, any>,
> = {
  Methods: Client extends keyof Commands
    ? V3MethodsRecord<Commands[Client]>
    : ExtractMethodsRecord<InstanceType<Clients[Client]>>;
};

/** How method invocation will look like after wrapping it into our hook */
export type BuildAwsmethodCall<Method extends (...args: any) => unknown> = (
  ...args: TupleArrayUnion<FilterUnknowns<ExtractOverloadedParams<Method>>>
) => Promise<ExtractAwsSdkResponse<ReturnType<Method>>>;
